import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, FormHelperText, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { ProductIds } from '@/constants/productIds';
import { Tutorial, CreateTutorialPayload, TutorialStatus } from '@/types/tutorials';
import { useCreateTutorialMutation, useUpdateTutorialMutation } from '@/api/tutorialsApi';
import { statusFormLabels } from '@/constants/tutorials';
import useSnackbar from '@/hooks/useSnackbar';

interface Props {
  open: boolean;
  onClose: () => void;
  tutorialToEdit?: Tutorial;
}

const TutorialFormDialog: React.FC<Props> = ({ open, onClose, tutorialToEdit }) => {
  const [openSnackbar] = useSnackbar();
  const [createTutorial, { isLoading: isCreating }] = useCreateTutorialMutation();
  const [updateTutorial, { isLoading: isUpdating }] = useUpdateTutorialMutation();

  const [formData, setFormData] = useState<CreateTutorialPayload>({
    youtubeUrl: '',
    title: '',
    duration: '',
    thumbnailUrl: '',
    productIds: [],
    status: '' as TutorialStatus,
    isActive: true,
  });

  const [isScraping, setIsScraping] = useState(false);

  useEffect(() => {
    if (tutorialToEdit) {
      setFormData({
        youtubeUrl: tutorialToEdit.youtubeUrl,
        title: tutorialToEdit.title,
        duration: tutorialToEdit.duration,
        thumbnailUrl: tutorialToEdit.thumbnailUrl,
        productIds: tutorialToEdit.productIds,
        status: tutorialToEdit.status,
        isActive: tutorialToEdit.isActive,
      });
    }
  }, [tutorialToEdit]);

  const fetchYoutubeMetadata = async (url: string) => {
    if (!url || !url.includes('youtube.com/') && !url.includes('youtu.be/')) return;

    setIsScraping(true);
    try {
      const response = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(url)}`);
      const data = await response.json();

      if (!data.error) {
        setFormData(prev => ({
          ...prev,
          title: prev.title || data.title,
          thumbnailUrl: prev.thumbnailUrl || data.thumbnail_url,
        }));
      }
    } catch (e) {
      console.error("Error scraping youtube metadata", e);
    } finally {
      setIsScraping(false);
    }
  };

  const handleUrlBlur = () => {
    // Only scrape if title and thumb are empty
    if (!formData.title || !formData.thumbnailUrl) {
      fetchYoutubeMetadata(formData?.youtubeUrl as string);
    }
  };

  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const handleProductIdsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;

    const productIds = Array.isArray(value)
      ? value
      : value
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

    setFormData(prev => ({
      ...prev,
      productIds: productIds as ProductIds[],
    }));
  };


  const handleSubmit = async () => {
    try {
      if (tutorialToEdit) {
        await updateTutorial({ id: tutorialToEdit.id, payload: formData });
        openSnackbar("Video actualizado correctamente", "success");
      } else {
        await createTutorial(formData);
        openSnackbar("Video creado correctamente", "success");
      }
      onClose();
    } catch (e) {
      console.error(e);
      openSnackbar("Error al guardar el video", "error");
    }
  };

  const isSaving = isCreating || isUpdating;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{tutorialToEdit ? 'Editar Video' : 'Nuevo Video'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="URL de YouTube"
            fullWidth
            value={formData.youtubeUrl}
            onChange={handleChange('youtubeUrl')}
            onBlur={handleUrlBlur}
            disabled={isSaving}
            InputProps={{
              endAdornment: isScraping ? <CircularProgress size={20} /> : null
            }}
          />
          <FormHelperText>Pega la URL y el sistema extraerá la información.</FormHelperText>

          <TextField
            label="Título"
            fullWidth
            value={formData.title}
            onChange={handleChange('title')}
            disabled={isSaving}
          />

          <TextField
            label="Duración (Ej: 04:30)"
            fullWidth
            value={formData.duration}
            onChange={handleChange('duration')}
            disabled={isSaving}
          />

          <TextField
            label="URL de Miniatura (Thumbnail)"
            fullWidth
            value={formData.thumbnailUrl}
            onChange={handleChange('thumbnailUrl')}
            disabled={isSaving}
          />
          <Stack direction='row-reverse' spacing={2}>
            <TextField
              label="Estado"
              fullWidth
              select
              value={formData.status || TutorialStatus.Pending}
              onChange={handleChange('status')}
              disabled={isSaving}
            >
              {statusFormLabels.map((status) => (
                <MenuItem key={status[0]} value={status[0]}>
                  {status[1]}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  name="isActive"
                  color="primary"
                />
              }
              label="Visible"
            />
          </Stack>

          <TextField
            select
            slotProps={{
              select: {
                multiple: true,
              }
            }}
            label="Mostrar en Páginas (Llaves)"
            fullWidth
            value={Array.isArray(formData.productIds) ? formData.productIds : []}
            onChange={handleProductIdsChange}
            disabled={isSaving}
            helperText="Si no seleccionas ninguna llave, el video no aparecerá en ninguna página automáticamente."
          >
            {Object.keys(ProductIds ?? {}).map((key) => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </TextField>


        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='error' variant='outlined' disabled={isSaving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSaving || !formData.youtubeUrl || !formData.title}>
          {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialFormDialog;
